import React, { useEffect, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';

const FAQ_SCHEMA_ATTR = 'data-faq-schema';

const buildFaqJsonLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: (faqs || []).map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.answer,
    },
  })),
});

/**
 * FAQ — renders a Radix Accordion of FAQs and injects FAQPage JSON-LD.
 *
 * @param {Object}  props
 * @param {Array}   props.faqs         Array of { id, question, answer }
 * @param {string}  [props.title]      Section heading (omitted when schemaOnly)
 * @param {string}  [props.description]Supporting copy under the heading
 * @param {boolean} [props.schemaOnly] If true, renders nothing visually but still injects schema
 * @param {string}  [props.id]         DOM id for the section
 * @param {string}  [props.className]  Extra classes on the wrapping <section>
 * @param {string}  [props.schemaKey]  Distinct key for the injected <script> (default: section id or 'default')
 */
const FAQ = ({
  faqs,
  title = 'Frequently asked questions',
  description,
  schemaOnly = false,
  id,
  className,
  schemaKey,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const jsonLd = useMemo(() => buildFaqJsonLd(faqs), [faqs]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!faqs || faqs.length === 0) return undefined;

    const key = schemaKey || id || 'default';
    const selector = `script[${FAQ_SCHEMA_ATTR}="${key}"]`;
    let script = document.querySelector(selector);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(FAQ_SCHEMA_ATTR, key);
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      const current = document.querySelector(selector);
      if (current) current.remove();
    };
  }, [faqs, jsonLd, id, schemaKey]);

  if (!faqs || faqs.length === 0) return null;
  if (schemaOnly) return null;

  const initial = prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 };
  const animate = { opacity: 1, y: 0 };

  return (
    <section
      id={id}
      className={cn(
        'bg-black text-white py-20 md:py-28 border-t border-white/10',
        className,
      )}
      aria-labelledby={id ? `${id}-heading` : undefined}
    >
      <div className="max-w-[900px] mx-auto px-6 md:px-12">
        <motion.div
          initial={initial}
          whileInView={animate}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="mb-12 md:mb-16 text-center"
        >
          <h2
            id={id ? `${id}-heading` : undefined}
            className="text-4xl md:text-5xl font-light tracking-tight mb-4"
          >
            {title}
          </h2>
          {description ? (
            <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto">
              {description}
            </p>
          ) : null}
        </motion.div>

        <motion.div
          initial={initial}
          whileInView={animate}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
        >
          <Accordion
            type="single"
            collapsible
            className="w-full divide-y divide-white/10 border-y border-white/10"
          >
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="border-b-0 px-1 md:px-2"
              >
                <AccordionTrigger
                  className={cn(
                    'py-6 text-left text-lg md:text-xl font-normal text-white',
                    'hover:no-underline hover:text-white/80',
                  )}
                >
                  <h3 className="text-base md:text-lg font-medium leading-snug pr-6">
                    {faq.question}
                  </h3>
                </AccordionTrigger>
                <AccordionContent className="pb-6 pt-0 text-sm md:text-base text-white/70 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
